import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useApi from '../../hook/useAPI';
import useWorkClock from '../../hook/useWorkClock';
import { DEVICE } from '../../utils/const';
import { TIME_LOG_STATE } from '../../constants/timeLog';
import './WorkClockShiftSetup.scss';

const DEVICE_LIST_LIMIT = 100;

function WorkClockShiftSetup() {
  const { getData } = useApi();
  const {
    clockState,
    selectedDeviceId,
    selectedDeviceName,
    operatorName,
    setSelectedDevice,
    setOperatorName,
    activeShift,
  } = useWorkClock({ loadLogs: false });

  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [highlightDevice, setHighlightDevice] = useState(false);
  const deviceSelectRef = useRef(null);
  const location = useLocation();

  const isOnShift = clockState !== TIME_LOG_STATE.OFF;
  const displayDeviceId = isOnShift ? activeShift?.deviceId : selectedDeviceId;
  const displayDeviceName = isOnShift ? activeShift?.deviceName : selectedDeviceName;
  const displayOperatorName = isOnShift ? activeShift?.notes : operatorName;

  useEffect(() => {
    const fetchDevices = async () => {
      setLoadingDevices(true);
      try {
        const response = await getData(
          `${DEVICE.GET_CURRENT_DEVICE_STATUS}?page=1&limit=${DEVICE_LIST_LIMIT}`,
          null,
          false
        );
        setDevices(response?.data || []);
      } catch (err) {
        console.error('Failed to load devices for work clock', err);
        setDevices([]);
      } finally {
        setLoadingDevices(false);
      }
    };

    fetchDevices();
  }, [getData]);

  useEffect(() => {
    if (!location.state?.highlightDeviceSelect || isOnShift) {
      return;
    }
    setHighlightDevice(true);
    deviceSelectRef.current?.focus();
    const timer = setTimeout(() => setHighlightDevice(false), 4000);
    return () => clearTimeout(timer);
  }, [isOnShift, location.state?.highlightDeviceSelect]);

  const handleDeviceChange = (event) => {
    const deviceId = event.target.value;
    const device = devices.find((row) => row._id === deviceId);
    setSelectedDevice(deviceId, device?.name || '');
    setHighlightDevice(false);
  };

  if (isOnShift) {
    return (
      <div className="work-clock-shift-setup work-clock-shift-setup--readonly">
        <p className="work-clock-shift-setup__title">Current shift device</p>
        <div className="work-clock-shift-setup__fields">
          <div className="work-clock-shift-setup__field">
            <span className="work-clock-shift-setup__label">Device</span>
            <span className="work-clock-shift-setup__value">{displayDeviceName || '—'}</span>
          </div>
          {displayOperatorName ? (
            <div className="work-clock-shift-setup__field">
              <span className="work-clock-shift-setup__label">Name</span>
              <span className="work-clock-shift-setup__value">{displayOperatorName}</span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`work-clock-shift-setup${highlightDevice ? ' work-clock-shift-setup--highlight' : ''}`}>
      <p className="work-clock-shift-setup__title">Shift setup</p>
      <p className="work-clock-shift-setup__hint">
        {highlightDevice
          ? 'Choose a device below, then use Clock In in the header.'
          : 'Select a device here before clocking in from the header.'}
      </p>
      <div className="work-clock-shift-setup__fields">
        <label className="work-clock-shift-setup__field">
          <span className="work-clock-shift-setup__label">Device</span>
          <select
            ref={deviceSelectRef}
            className="form-select work-clock-shift-setup__select"
            value={displayDeviceId || ''}
            onChange={handleDeviceChange}
            disabled={loadingDevices}
          >
            <option value="">
              {loadingDevices ? 'Loading devices…' : 'Select device'}
            </option>
            {devices.map((device) => (
              <option key={device._id} value={device._id}>
                {device.name}
              </option>
            ))}
          </select>
        </label>
        <label className="work-clock-shift-setup__field">
          <span className="work-clock-shift-setup__label">Name (optional)</span>
          <input
            type="text"
            className="form-control work-clock-shift-setup__input"
            value={operatorName}
            onChange={(e) => setOperatorName(e.target.value)}
            placeholder="Operator or note"
            maxLength={120}
          />
        </label>
      </div>
    </div>
  );
}

export default WorkClockShiftSetup;
